import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { dateTime, kg, money, serviceLabels } from "@/lib/format";
import type { Address, Shipment } from "@/lib/types";
import { Alert, Button, Card, PageHeader, Skeleton, StatusBadge } from "@/components/ui";
import { Timeline } from "@/components/Timeline";

const OPEN_STATUSES = ["booked", "awaiting_collection", "collected", "in_transit", "at_hub", "out_for_delivery"];

export function ShipmentDetail() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const queryClient = useQueryClient();

  const { data: shipment, isLoading } = useQuery({
    queryKey: ["shipment", id],
    queryFn: () => api.get<Shipment>(`/v1/shipments/${id}`),
    // Poll while the parcel is moving, stop once it is not.
    refetchInterval: (query) =>
      query.state.data && OPEN_STATUSES.includes(query.state.data.status) ? 60_000 : false,
  });

  const refresh = useMutation({
    mutationFn: () => api.post<Shipment>(`/v1/shipments/${id}/refresh-tracking`),
    onSuccess: (updated) => queryClient.setQueryData(["shipment", id], updated),
  });

  const cancel = useMutation({
    mutationFn: () => api.post<Shipment>(`/v1/shipments/${id}/cancel`),
    onSuccess: (updated) => {
      queryClient.setQueryData(["shipment", id], updated);
      queryClient.invalidateQueries({ queryKey: ["shipments"] });
    },
  });

  if (isLoading) {
    return (
      <Card>
        <Skeleton className="h-64 w-full" />
      </Card>
    );
  }
  if (!shipment) {
    return <Alert>This shipment could not be found.</Alert>;
  }

  const cancellable = shipment.status === "booked" || shipment.status === "awaiting_collection";

  return (
    <>
      {params.get("booked") && (
        <div className="mb-5">
          <Alert tone="good">
            Booked. The carrier has the collection and your waybill is ready.
          </Alert>
        </div>
      )}

      <PageHeader
        title={shipment.waybill}
        subtitle={serviceLabels[shipment.service_level] ?? shipment.service_level}
        action={
          <div className="flex flex-wrap gap-2">
            {shipment.label_url && (
              <a href={shipment.label_url} target="_blank" rel="noreferrer">
                <Button variant="secondary">Download waybill</Button>
              </a>
            )}
            <Button variant="secondary" loading={refresh.isPending} onClick={() => refresh.mutate()}>
              Refresh tracking
            </Button>
            {cancellable && (
              <Button variant="danger" loading={cancel.isPending} onClick={() => cancel.mutate()}>
                Cancel
              </Button>
            )}
          </div>
        }
      />

      {cancel.isError && (
        <div className="mb-4">
          <Alert>This shipment can no longer be cancelled. Contact the carrier directly.</Alert>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card>
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
              <StatusBadge status={shipment.status} />
              <p className="text-sm text-ink-400">
                {shipment.delivered_at
                  ? `Delivered ${dateTime(shipment.delivered_at)}`
                  : shipment.estimated_delivery_at
                    ? `Estimated ${dateTime(shipment.estimated_delivery_at)}`
                    : "Estimate pending"}
              </p>
            </div>
            <Timeline events={shipment.events ?? []} />
          </Card>

          <Card>
            <h2 className="mb-4 text-lg font-semibold tracking-tight">Parcels</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-ink-400">
                  <tr>
                    <th className="pb-2">Contents</th>
                    <th className="pb-2">Dimensions</th>
                    <th className="pb-2 text-right">Weight</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-200">
                  {shipment.parcels.map((parcel, index) => (
                    <tr key={index}>
                      <td className="py-2.5">{parcel.description || `Parcel ${index + 1}`}</td>
                      <td className="whitespace-nowrap py-2.5 text-ink-600">
                        {parcel.length_mm} x {parcel.width_mm} x {parcel.height_mm} mm
                      </td>
                      <td className="py-2.5 text-right">{kg(parcel.weight_g)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div className="space-y-5">
          <Card>
            <h2 className="mb-4 text-lg font-semibold tracking-tight">Route</h2>
            <AddressBlock title="Collection" address={shipment.collection} />
            {(shipment.requested_collection_after || shipment.requested_collection_before) && (
              <p className="mt-2 text-xs text-ink-500">
                Requested {shipment.requested_collection_after ?? "any time"}
                {" – "}
                {shipment.requested_collection_before ?? "any time"}
              </p>
            )}
            <div className="my-4 border-t border-ink-200" />
            <AddressBlock title="Delivery" address={shipment.delivery} />
            {(shipment.requested_delivery_after || shipment.requested_delivery_before) && (
              <p className="mt-2 text-xs text-ink-500">
                Requested {shipment.requested_delivery_after ?? "any time"}
                {" – "}
                {shipment.requested_delivery_before ?? "any time"}
              </p>
            )}
          </Card>

          <Card>
            <h2 className="mb-4 text-lg font-semibold tracking-tight">Charges</h2>
            <dl className="space-y-2 text-sm">
              <Row label="Shipping" value={money(shipment.price_cents - shipment.vat_cents - shipment.insurance_cents)} />
              {shipment.insurance_cents > 0 && <Row label="Insurance" value={money(shipment.insurance_cents)} />}
              <Row label="VAT" value={money(shipment.vat_cents)} />
              <div className="flex justify-between border-t border-ink-200 pt-2 font-semibold">
                <dt>Total</dt>
                <dd>{money(shipment.price_cents)}</dd>
              </div>
            </dl>
            {shipment.reference && <p className="mt-4 text-xs text-ink-400">Your reference: {shipment.reference}</p>}
            <p className="mt-4 text-xs text-ink-400">
              Share this link with your customer:{" "}
              <Link className="text-brand" to={`/track/${shipment.waybill}`}>
                /track/{shipment.waybill}
              </Link>
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}

function AddressBlock({ title, address }: { title: string; address: Address }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-ink-400">{title}</p>
      <p className="mt-1.5 text-sm font-medium">{address.contact_name}</p>
      {address.company_name && <p className="text-sm text-ink-600">{address.company_name}</p>}
      <p className="mt-1 text-sm text-ink-600">
        {address.street}
        {address.complex ? `, ${address.complex}` : ""}
        <br />
        {address.suburb}, {address.city}
        <br />
        {address.province} {address.postal_code}
      </p>
      <p className="mt-1 text-sm text-ink-400">{address.phone}</p>
      {address.instructions && <p className="mt-2 text-xs text-ink-400">{address.instructions}</p>}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <dt className="text-ink-400">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
